import Icon from '@components/utils/icons/icon';
import { StyledProps, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { useProgressContext } from '@lib/pimster-stories/contexts/ProgressContext';
import { useVideoPlayerContext } from '@lib/pimster-stories/contexts/VideoPlayerContext';
import Player from '@node_modules/video.js/dist/types/player';
import { FC, useCallback } from 'react';

import { VideoJS, VideoJSOptions } from './VideoJS';

type Props = {
    poster?: string;
    src?: VideoJSOptions['sources'];
    onReady?: VideoJSOptions['onReady'];
    onDispose?: VideoJSOptions['onDispose'];
    style?: React.CSSProperties;
};

const Container = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    position: relative;
`;

const ButtonCircle = styled.button<StyledProps<{ visible: boolean }>>`
    z-index: 901;
    cursor: pointer;
    pointer-events: auto;
    position: fixed;
    border-radius: 100%;
    top: 50%;
    right: 50%;
    transform: translate(50%, -50%);
    background-color: rgba(0, 0, 0, 0.4);
    align-items: center;
    justify-content: center;
    transition: all 1s ease-in;
    display: ${({ visible }) => (visible ? 'flex' : 'none')};
    height: ${({ theme, visible }) => (visible ? theme.spacing.xxl : '0')};
    width: ${({ theme, visible }) => (visible ? theme.spacing.xxl : '0')};
    font: ${({ theme }) => theme.fonts.subtitle4};
    &:active {
        background-color: rgba(0, 0, 0, 0.6);
        height: ${(props) => `calc(1.2 * ${props.theme.spacing.xxl})`};
        width: ${(props) => `calc(1.2 * ${props.theme.spacing.xxl})`};
        transition: all 0.2s ease-in;
    }
`;

const VideoPlayer: FC<Props> = ({ poster, src, onReady, onDispose, style }) => {
    const theme = useTheme();
    const { isPaused } = useProgressContext();
    const { isMuted, playVideo, onPlayerReady } = useVideoPlayerContext();

    const handleOnReady = useCallback(
        (player: Player) => {
            onPlayerReady(player);
            onReady?.(player);
        },
        [onPlayerReady, onReady]
    );

    const handleOnDispose = useCallback(
        (player: Player) => {
            onDispose?.(player);
        },
        [onDispose]
    );

    return (
        <Container className='video_container'>
            <VideoJS
                options={{
                    poster,
                    controls: false,
                    muted: isMuted,
                    playsinline: true,
                    preload: 'metadata',
                    sources: src,
                    onReady: handleOnReady,
                    onDispose: handleOnDispose,
                }}
                style={style}
            />
            <ButtonCircle visible={isPaused} onClick={playVideo}>
                <Icon
                    size='medium'
                    name='play_arrow'
                    color={theme.palette.common.white}
                />
            </ButtonCircle>
        </Container>
    );
};

export default VideoPlayer;
